import { useState, useEffect, useCallback } from 'react';
import { customersAPI, reportsAPI, downloadBlob } from '@/lib/api';
import RecordAdvanceDialog from '@/components/RecordAdvanceDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  UserCircle, Buildings, Receipt, Money, ArrowCounterClockwise, ShoppingCart, FileText, DownloadSimple,
} from '@phosphor-icons/react';

const fmt = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-';
const fmtD = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

/**
 * Reusable customer drill-down dialog used across Analytics, Reports, Invoices,
 * Sales Orders, Payments & Customers. Pass either `customerId` (id OR zoho_id)
 * and/or `customerName` — it resolves robustly via /customers/drilldown.
 */
export default function CustomerDrillDownDialog({ open, onOpenChange, customerId = '', customerName = '', customerPhone = '' }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('ledger');
  const [showAdvance, setShowAdvance] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const fetchDetail = useCallback(async (startDate = '', endDate = '') => {
    setLoading(true);
    try {
      const params = {};
      if (customerId) params.customer_id = customerId;
      if (customerName) params.name = customerName;
      if (customerPhone) params.phone = customerPhone;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await customersAPI.drilldown(params);
      setDetail(data);
    } catch { toast.error('Failed to load customer transactions'); }
    finally { setLoading(false); }
  }, [customerId, customerName, customerPhone]);

  useEffect(() => {
    if (open) { setTab('ledger'); setFrom(''); setTo(''); setDetail(null); fetchDetail(); }
  }, [open, fetchDetail]);

  const displayName = detail?.customer?.display_name || customerName || 'Customer';
  const isBiz = detail?.customer?.customer_type === 'business';

  const handleLedgerExport = async (format) => {
    try {
      const params = { format };
      if (customerId) params.customer_id = customerId;
      if (customerName) params.name = customerName;
      if (customerPhone) params.phone = customerPhone;
      if (from) params.start_date = from;
      if (to) params.end_date = to;
      const res = await reportsAPI.customerLedgerExport(params);
      downloadBlob(res, `ledger_statement.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      toast.success(`Ledger ${format.toUpperCase()} downloaded`);
    } catch { toast.error('Failed to export ledger'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="customer-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center justify-between gap-2 text-lg">
            <span className="flex items-center gap-2">
              {isBiz ? <Buildings size={20} className="text-blue-600" /> : <UserCircle size={20} />}
              {displayName}
            </span>
            <Button variant="outline" size="sm" className="mr-6" onClick={() => setShowAdvance(true)} data-testid="drill-record-advance-btn">
              <Money size={14} className="mr-1.5" /> Record Advance
            </Button>
          </DialogTitle>
        </DialogHeader>

        {loading && !detail ? (
          <div className="py-12 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
        ) : detail ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3 mt-2">
              <div className="bg-[#EBF3FA] rounded-lg p-3 text-center">
                <p className="text-[10px] text-[#64748B] uppercase">Invoiced</p>
                <p className="text-base font-bold text-[#0F4C81]" data-testid="drill-total-invoiced">{fmt(detail.summary.total_invoiced)}</p>
                <p className="text-[10px] text-[#64748B]">{detail.summary.invoice_count} invoices</p>
              </div>
              <div className="bg-[#E8F5E9] rounded-lg p-3 text-center">
                <p className="text-[10px] text-[#64748B] uppercase">Payments</p>
                <p className="text-base font-bold text-green-700">{fmt(detail.summary.total_payments)}</p>
                <p className="text-[10px] text-[#64748B]">{detail.summary.payment_count} payments</p>
              </div>
              <div className="bg-[#FFF3E0] rounded-lg p-3 text-center">
                <p className="text-[10px] text-[#64748B] uppercase">Credit Notes</p>
                <p className="text-base font-bold text-orange-700">{fmt(detail.summary.total_credit_notes)}</p>
                <p className="text-[10px] text-[#64748B]">{detail.summary.cn_count} CNs</p>
              </div>
              <div className={`rounded-lg p-3 text-center ${detail.summary.outstanding > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <p className="text-[10px] text-[#64748B] uppercase">Outstanding</p>
                <p className={`text-base font-bold ${detail.summary.outstanding > 0 ? 'text-[#DC2626]' : 'text-green-700'}`}>{fmt(detail.summary.outstanding)}</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mt-3 border-b border-[#E2E8F0] flex-wrap">
              {[
                { key: 'ledger', label: 'Ledger Statement', icon: FileText },
                { key: 'invoices', label: `Invoices (${detail.summary.invoice_count})`, icon: Receipt },
                { key: 'payments', label: `Payments (${detail.summary.payment_count})`, icon: Money },
                { key: 'credit_notes', label: `Credit Notes (${detail.summary.cn_count})`, icon: ArrowCounterClockwise },
                { key: 'sales_orders', label: `Sales Orders (${detail.summary.so_count})`, icon: ShoppingCart },
              ].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${tab === t.key ? 'border-[#0F4C81] text-[#0F4C81]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
                  data-testid={`drill-tab-${t.key}`}
                >
                  <t.icon size={14} /> {t.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="py-8 text-center"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <div className="mt-3">
                {tab === 'ledger' && (
                  <div className="space-y-3">
                    <div className="flex items-end gap-3">
                      <div>
                        <label className="text-[10px] text-[#64748B] block mb-1">FROM</label>
                        <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="drill-ledger-from" />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#64748B] block mb-1">TO</label>
                        <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 text-xs w-36" data-testid="drill-ledger-to" />
                      </div>
                      <Button size="sm" variant="outline" onClick={() => fetchDetail(from, to)} className="h-8" data-testid="drill-ledger-filter">Filter</Button>
                      <div className="ml-auto flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleLedgerExport('excel')} className="h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="drill-ledger-excel"><DownloadSimple size={14} className="mr-1" />Excel</Button>
                        <Button size="sm" variant="outline" onClick={() => handleLedgerExport('pdf')} className="h-8 text-red-700 border-red-200 hover:bg-red-50" data-testid="drill-ledger-pdf"><DownloadSimple size={14} className="mr-1" />PDF</Button>
                      </div>
                    </div>
                    <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-[#0F4C81] text-white text-[10px] uppercase">
                            <th className="text-left p-2">Date</th><th className="text-left p-2">Type</th>
                            <th className="text-left p-2">Number</th><th className="text-left p-2">Description</th>
                            <th className="text-right p-2">Debit</th><th className="text-right p-2">Credit</th><th className="text-right p-2">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.ledger?.length > 0 ? detail.ledger.map((e, i) => (
                            <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                              <td className="p-2">{fmtD(e.date)}</td>
                              <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${e.type === 'Invoice' ? 'bg-blue-50 text-blue-700' : e.type === 'Payment' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}`}>{e.type}</span></td>
                              <td className="p-2 font-mono">{e.number}</td>
                              <td className="p-2 text-[#64748B]">{e.description}</td>
                              <td className="p-2 text-right font-medium">{e.debit > 0 ? fmt(e.debit) : ''}</td>
                              <td className="p-2 text-right font-medium text-green-700">{e.credit > 0 ? fmt(e.credit) : ''}</td>
                              <td className={`p-2 text-right font-bold ${e.balance > 0 ? 'text-[#DC2626]' : 'text-green-700'}`}>{fmt(e.balance)}</td>
                            </tr>
                          )) : <tr><td colSpan={7} className="text-center py-4 text-[#64748B]">No transactions found</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {tab === 'invoices' && (
                  <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#F8FAFC] text-[#64748B] text-[10px] font-medium uppercase">
                        <th className="text-left p-2">Invoice #</th><th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Location</th><th className="text-right p-2">Amount</th>
                        <th className="text-right p-2">Balance</th><th className="text-center p-2">Status</th>
                      </tr></thead>
                      <tbody>
                        {detail.invoices.map(inv => (
                          <tr key={inv.id} className="border-t border-[#E2E8F0]">
                            <td className="p-2 font-mono font-medium">{inv.invoice_number}</td>
                            <td className="p-2">{fmtD(inv.date)}</td>
                            <td className="p-2">{inv.clinic_name || inv.location_name}</td>
                            <td className="p-2 text-right font-medium">{fmt(inv.grand_total)}</td>
                            <td className="p-2 text-right text-[#DC2626]">{fmt(inv.balance_due ?? inv.balance ?? 0)}</td>
                            <td className="p-2 text-center"><span className={`px-1.5 py-0.5 rounded text-[10px] ${inv.payment_status === 'paid' ? 'bg-green-50 text-green-700' : inv.payment_status === 'credit_noted' ? 'bg-orange-50 text-orange-700' : 'bg-yellow-50 text-yellow-700'}`}>{inv.payment_status || inv.status}</span></td>
                          </tr>
                        ))}
                        {!detail.invoices.length && <tr><td colSpan={6} className="text-center py-4 text-[#64748B]">No invoices</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                {tab === 'payments' && (
                  <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#F8FAFC] text-[#64748B] text-[10px] font-medium uppercase">
                        <th className="text-left p-2">Payment #</th><th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Invoice</th><th className="text-left p-2">Mode</th><th className="text-right p-2">Amount</th>
                      </tr></thead>
                      <tbody>
                        {detail.payments.map(pay => (
                          <tr key={pay.id} className="border-t border-[#E2E8F0]">
                            <td className="p-2 font-mono font-medium">{pay.payment_number}</td>
                            <td className="p-2">{fmtD(pay.payment_date)}</td>
                            <td className="p-2 font-mono">{pay.invoice_number}</td>
                            <td className="p-2 capitalize">{pay.payment_mode}</td>
                            <td className="p-2 text-right font-medium text-green-700">{fmt(pay.amount)}</td>
                          </tr>
                        ))}
                        {!detail.payments.length && <tr><td colSpan={5} className="text-center py-4 text-[#64748B]">No payments</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                {tab === 'credit_notes' && (
                  <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#F8FAFC] text-[#64748B] text-[10px] font-medium uppercase">
                        <th className="text-left p-2">CN #</th><th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Invoice</th><th className="text-right p-2">Amount</th><th className="text-left p-2">Reason</th>
                      </tr></thead>
                      <tbody>
                        {detail.credit_notes.map(cn => (
                          <tr key={cn.id} className="border-t border-[#E2E8F0]">
                            <td className="p-2 font-mono font-medium">{cn.cn_number}</td>
                            <td className="p-2">{fmtD(cn.cn_date)}</td>
                            <td className="p-2 font-mono">{cn.invoice_number}</td>
                            <td className="p-2 text-right font-medium text-[#DC2626]">{fmt(cn.grand_total)}</td>
                            <td className="p-2 text-[#64748B]">{cn.reason}</td>
                          </tr>
                        ))}
                        {!detail.credit_notes.length && <tr><td colSpan={5} className="text-center py-4 text-[#64748B]">No credit notes</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}

                {tab === 'sales_orders' && (
                  <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-[#F8FAFC] text-[#64748B] text-[10px] font-medium uppercase">
                        <th className="text-left p-2">SO #</th><th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Location</th><th className="text-right p-2">Amount</th><th className="text-center p-2">Status</th>
                      </tr></thead>
                      <tbody>
                        {detail.sales_orders.map(so => (
                          <tr key={so.id} className="border-t border-[#E2E8F0]">
                            <td className="p-2 font-mono font-medium">{so.so_number}</td>
                            <td className="p-2">{fmtD(so.order_date || so.date)}</td>
                            <td className="p-2">{so.clinic_name || so.location_name}</td>
                            <td className="p-2 text-right font-medium">{fmt(so.grand_total)}</td>
                            <td className="p-2 text-center"><span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{so.status}</span></td>
                          </tr>
                        ))}
                        {!detail.sales_orders.length && <tr><td colSpan={5} className="text-center py-4 text-[#64748B]">No sales orders</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="py-12 text-center text-[#64748B]">No data</div>
        )}
      </DialogContent>
      <RecordAdvanceDialog
        open={showAdvance}
        onOpenChange={setShowAdvance}
        prefillCustomer={{ id: customerId || detail?.customer?.id || '', name: displayName, phone: customerPhone }}
        onSaved={() => fetchDetail(from, to)}
      />
    </Dialog>
  );
}
